<script lang="ts" context="module">
  type PreloadOptions = '' | 'auto' | 'metadata' | 'none';

  interface NormalVideoData {
    type: 'video';
    sources: {
      src: string;
      type: string;
    }[];
  }

  interface YoutubeVideoData {
    type: 'youtube';
    id: string;
  }

  export type VideoData = NormalVideoData | YoutubeVideoData;

  export interface VideoOptions {
    videoData: VideoData;
    aspectRatio?: string;
    preload?: PreloadOptions;
    poster?: string;
    autopause?: boolean;
  }
</script>

<script lang="ts">
  import {
    ClickToPlay,
    Controls,
    Hls,
    LoadingScreen,
    PlaybackControl,
    Player,
    Poster,
    Spinner,
    Ui,
    Youtube,
  } from '@vime/svelte';
  import Icon from './Icon.svelte';

  export let options: VideoOptions;

  // Hack: HLS.js config causes preload to happen despite falsey preload hint
  // We will set the HLS autoStartLoad to false and manually call startLoad when play button is pressed for the first time.
  let firstLoad = false;

  const hlsConfig = {
    autoStartLoad: false,
    maxMaxBufferLength: 60, // buffer 60s at most
  };

  let hlsProvider;

  function play() {
    if (!firstLoad && options.videoData.type === 'video') {
      hlsProvider
        .getAdapter()
        .then((adapter) => {
          return adapter.getInternalPlayer();
        })
        .then((hlsPlayer) => {
          // May not exist on devices that natively support HLS e.g. Safari iPhone
          hlsPlayer?.startLoad();
        });
    }

    firstLoad = true;
  }

  let posterLoaded = false;

  function onPosterLoaded() {
    posterLoaded = true;
  }
</script>

<Player
  class={$$restProps.class}
  aspectRatio={options.aspectRatio}
  autopause={options.autopause}
  playsinline
  currentPoster={options.poster}
  on:vmPlay={play}
>
  {#if options.videoData.type === 'video'}
    <Hls
      bind:this={hlsProvider}
      version="latest"
      preload={options.preload}
      config={hlsConfig}
    >
      {#each options.videoData.sources as source}
        <!-- By right setting data-src should be enough, but it bugs out on Safari iOS (iPhone) (15 Jan 2021) -->
        <!-- Therefore we set both -->
        <!-- See: https://github.com/vime-js/vime/issues/153 -->
        <source data-src={source.src} src={source.src} type={source.type} />
      {/each}
    </Hls>
  {:else if options.videoData.type === 'youtube'}
    <Youtube videoId={options.videoData.id} />
  {/if}

  <Ui>
    {#if posterLoaded && !firstLoad}
      <div class="bigPlayContainer">
        <Icon name="eva-arrow-right-outline" color="light-3" class="playIcon" />
      </div>
    {/if}

    <LoadingScreen hideDots={posterLoaded} />

    <Poster on:vmLoaded={onPosterLoaded} />
    <Spinner />
    <ClickToPlay useOnMobile />

    <Controls hideOnMouseLeave>
      <PlaybackControl />
    </Controls>
  </Ui>
</Player>

<style lang="scss" global>
  @import '../node_modules/@vime/core/themes/default';

  .videoPlayer {
    :global(.vjs-big-play-button) {
      height: 1.5em;
      width: 1.5em;
      border-radius: 50%;
      margin-left: -0.75em;
    }
  }

  .bigPlayContainer {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 3rem;
    z-index: 10; // Go above poster

    :global(.playIcon) {
      text-shadow: 0 0 2px black;
    }
  }
</style>
